import React, { FC, useEffect, useMemo, useState } from "react";
import ColorInput from "./ColorInput";
import { Circle, CircleData, Step, StepOption } from "../../../types/wizard";
import { addTarget, saveEmblemData, setBorderColor, setThreadColors, validateWizardSteps, wizardStore } from "../../../stores/wizard";
import ColorSelector from "../ColorSelector";
import { Steps } from "../../../constants";
import { useSnapshot } from "valtio";
import { findClosestColor } from "../../../utils/findColor";

interface ColorManagerProps {
  step: Step;
  isEditing?: boolean;
}

const ColorManager: FC<ColorManagerProps> = ({
  step,
  isEditing
}) => {
  const wizSnap = useSnapshot(wizardStore);
  const emblemData = wizSnap?.emblemData || {};
  const [colors, setColors] = useState<CircleData[]>([]);
  const [activePickerIndex, setActivePickerIndex] = useState<number | null>(null);
  const [selectedSingleColor, setSelectedSingleColor] = useState<StepOption | null>(wizSnap.emblemData[step.id]);
  const canAddNewColors = useMemo(() => {
    const maxColors = step?.numberOfColorsAllowedMax;
    const items = emblemData[step.id]?.items ?? [];
    return maxColors ? items.length < maxColors : true;
  }, [step?.id, emblemData, step?.numberOfColorsAllowedMax]);
  const singleSelection = useMemo(() => step?.id === Steps.BorderColor || step.id === Steps.SewingGuideColor, [step?.id]);
  const target = wizSnap?.target[step.id];
  const pickedColor = target?.circle;
  const colorToEdit = isEditing ? step?.options?.find(o => o.htmlColor == pickedColor.color) : null;
  const palleteColor = useMemo(() => {
    if (!pickedColor?.rgba) return null;
    return findClosestColor(pickedColor.rgba, step.options);
  }, [pickedColor?.rgba, step.options]);
  const closest = (colorToEdit && palleteColor?.id !== colorToEdit?.id) ? colorToEdit : palleteColor;
  const isInvalid = wizSnap.errors[step?.id]?.length;
  const border = isInvalid ? 'border border-solid border-[#a61613]' : 'border border-solid border-[#ccc]';
  const [colorInstruction, setColorInstruction] = useState<string>("");

  useEffect(() => {
    const savedColors = emblemData[step.id]?.items || [];
    setColors(savedColors);

    if (isEditing && wizSnap.editedCircleColor) {
      const existingItem = savedColors.find(
        (item: CircleData) =>
          item.circle?.id === wizSnap.editedCircleColor?.circle?.id
      );
      setColorInstruction(existingItem?.option?.instructions || "");
    } else if (isEditing && pickedColor) {
      const existingItem = savedColors.find(
        (item: CircleData) =>
          item.circle?.x === pickedColor.x && item.circle?.y === pickedColor.y
      );
      setColorInstruction(existingItem?.option?.instructions || "");
    } else {
      setColorInstruction("");
    }
  }, [emblemData, step.id, isEditing, pickedColor, wizSnap.editedCircleColor]);

  useEffect(() => {
    if (target) {
      const existTarget = colors?.find(c => c.circle?.id === target.circle.id);
      if (!existTarget) {
        addInput(target.circle);
        setActivePickerIndex(colors?.length)
      }
    }
  }, [target, step.id]);

  const addInput = (target?: Circle) => {
    setColors((prev) => [
      ...prev,
      {
        option: {
          id: target?.id ?? Date.now(),
          name: "",
          thumbnailImage: null,
          longName: null,
          description: null,
          hoverImage: null,
          code: null,
          colorCodePMS: null,
          shortDescription: null,
          longDescription: null,
          image: null,
          htmlColor: null,
          colorCodeMadeira: null,
          colorCodeRobisonAnton: null,
          instructions: null,
          updateSteps: false,
          nestedListOptions: null,
          x: null,
          y: null,
          clientId: null
        },
        color: "",
        stepId: step.id,
      },
    ]);
  };

  const handleColorChange = (index: number, newColorData: Partial<CircleData>) => {
    setColors((prev) =>
      prev.map((colorData, i) => {
        if (i === index) {
          return {
            ...colorData,
            ...newColorData,
          };
        }
        return colorData;
      })
    );
  };

  const toggleColorPicker = (index: number) => {
    setActivePickerIndex((prev) => (prev === index ? null : index));
  };

  const handleSingleColorSelect = (color: StepOption) => {
    saveEmblemData(step.id, color);
    setBorderColor(color, step?.id);
    setSelectedSingleColor(color);
    setActivePickerIndex(null);
    validateWizardSteps(step.id);
  };

  const onSave = (color: CircleData, index: number) => {
    const currentItems = wizSnap.emblemData[step.id]?.items || [];
    const findIndex = currentItems.findIndex((c: CircleData, i: number) => {
      if (isEditing && c.circle)
        return c.circle.x === pickedColor.x && c.circle?.y === pickedColor.y;

      return !c.circle && i === index;
    });

    const existingCircle =
      currentItems[findIndex]?.circle;

    const option: StepOption = {
      ...color.option,
      instructions: colorInstruction,
      clientId: findIndex !== -1 ? (findIndex + 1) : currentItems?.length + 1
    }

    const newItem: CircleData = {
      circle: existingCircle
        ? {
          ...existingCircle,
          color: color.option.htmlColor ?? "",
          rgba: existingCircle.rgba,
        }
        : closest
          ? pickedColor
          : undefined,
      color: color.option.htmlColor ?? "",
      option: option,
      stepId: step.id,
    };

    const updatedItems =
      findIndex !== -1
        ? [
          ...currentItems.slice(0, findIndex),
          { ...currentItems[findIndex], ...newItem },
          ...currentItems.slice(findIndex + 1),
        ]
        : [...currentItems, newItem];

    saveEmblemData(step.id, { items: updatedItems });
    setThreadColors(newItem, findIndex)

    if (closest && closest.htmlColor) {
      addTarget(step.id, {} as Circle);
    }

    updateIndications(newItem);
  };

  const updateIndications = (color: CircleData) => {
    // Find if exists a 3D emblem indication using the same color
    const indications = wizSnap.emblemData[Steps.Emblem3d]?.items || [];
    const updatedIndications = indications.filter((i: CircleData) => i.option.uniqueId !== color.circle?.id);

    if (updatedIndications.length !== indications.length) {
      saveEmblemData(Steps.Emblem3d, { items: updatedIndications });
    }

    validateWizardSteps(step.id);
  }

  return (<React.Fragment>
    <div className="relative rounded bg-white">
      {singleSelection ? (
        <div className={`w-full ${singleSelection && ''}`}>
          {activePickerIndex !== 0 &&
            <>
              {!wizSnap?.loading && <label className="block uppercase font-bold mb-2.5">{step.title?.replace(/color/gi, 'colour')}</label>}
              <div className="flex flex-col space-y-2">
                <ColorInput
                  color={selectedSingleColor}
                  border={border}
                  toggleEdit={() => setActivePickerIndex(0)}
                  isLoading={wizSnap?.loading}
                />
                {isInvalid &&
                  <div className="text-[#a61613] text-sm leading-4 mt-2">
                    <span>{wizSnap.errors[step?.id][0]}</span>
                  </div>
                }
              </div>
              {selectedSingleColor && <div className="flex flex-row justify-end">
                <button
                  className="outline-none hover:outline-none focus:outline-none border-0 underline text-sm text-error px-0 py-1 bg-inherit"
                  onClick={() => setSelectedSingleColor(null)}>
                  Remove
                </button>
              </div>
              }
            </>
          }
          {activePickerIndex === 0 && (
            <ColorSelector
              step={step}
              singleSelection={singleSelection}
              colorOption={selectedSingleColor}
              onSelect={(color) => handleSingleColorSelect(color.option)}
              onCancelClick={() => toggleColorPicker(0)}
              canAddNewColors={canAddNewColors}
              index={0}
            />
          )}
        </div>
      ) : (
        <>
          {closest && closest.htmlColor &&
            <ColorSelector
              step={step}
              isClosestColor={true}
              colorOption={closest}
              onSelect={(color) => {
                handleColorChange(colors?.length ? colors?.length + 1 : 0, color)
                onSave(color, colors?.length ? colors?.length + 1 : 0)
              }}
              onCancelClick={() => addTarget(step.id, {} as Circle)}
              canAddNewColors={canAddNewColors}
              colorInstruction={(e) => setColorInstruction(e)}
              initialInstructions={colorInstruction}
            />
          }
          {/* {colors.map((color, index) => (
            <div key={color.circle?.id || index} className="flex flex-col space-y-2">
              {activePickerIndex !== index ? (
                <>
                  <label className="block uppercase font-bold mt-2">{`COLOR ${index + 1}`}</label>
                  <ColorInput
                    color={color.option}
                    toggleEdit={() => setActivePickerIndex(index)}
                  />
                  <div className="flex flex-row justify-end">
                    <button
                      className="outline-none hover:outline-none border-0 underline text-sm text-error px-0 py-1 bg-inherit"
                      onClick={() => removeInput(index, color)}
                    >
                      Remove
                    </button>
                  </div>
                </>
              ) : (
                <ColorSelector
                  step={step}
                  circle={color.circle}
                  colorOption={color.option}
                  index={index}
                  onSelect={(colorData) => {
                    handleColorChange(index, colorData);
                    toggleColorPicker(index);
                    onSave(colorData, index);
                  }}
                  onCancelClick={() => toggleColorPicker(index)}
                />
              )}
            </div>
          ))} */}
          {/* {!closest && (
            <div className="flex flex-col justify-left py-4">
              <div className="flex flex-row">
                <button
                  className="btn-file bg-black text-white underline rounded"
                  onClick={() => addInput()}
                  disabled={!canAddNewColors}
                >
                  Add New Color
                </button>
              </div>
            </div>
          )} */}
        </>
      )}
    </div>
  </React.Fragment>);
};

export default ColorManager;